import { useEffect, useId, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, PencilLine, Plus, RefreshCw } from 'lucide-react'

import { ApiError } from '../../services/api/client'
import { Button } from '../../ui/Button/Button'
import { Switch } from '../../ui/Switch/Switch'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import type {
  SpecialRule,
  SpecialRulesResponse,
} from './assistant-settings-contracts'
import type { AssistantSettingsRepository } from './assistant-settings-repository'
import { RuleDialog } from './RuleDialog'
import panel from './SettingsSection.module.css'
import styles from './AiRulesSettings.module.css'

type VisibleScope = 'GENERAL' | 'VOICE_ONLY'
type RuleAction =
  | { kind: 'CREATE'; content: string }
  | { kind: 'UPDATE'; rule: SpecialRule; content: string }
  | { kind: 'STATUS'; rule: SpecialRule; status: 'ACTIVE' | 'INACTIVE' }
  | { kind: 'DELETE'; rule: SpecialRule }

type PendingReconciliation = {
  tenantId: string
  revision: number
  fingerprint: string
}

class VoiceReconciliationPendingError extends Error {}

/** The owner's limits: 50 rules not deleted, 200 stored including deleted. */
export const RULES_IN_USE_LIMIT = 50
export const RULES_STORED_LIMIT = 200

const COPY: Record<
  VisibleScope,
  { eyebrow: string; title: string; description: string; empty: string }
> = {
  VOICE_ONLY: {
    eyebrow: 'Voice',
    title: 'Voice Rules',
    description: 'Guidance Ava follows on phone calls.',
    empty: 'No voice rules yet.',
  },
  GENERAL: {
    eyebrow: 'Calls and texts',
    title: 'General Rules',
    description: 'Guidance Ava follows on calls and in text messages.',
    empty: 'No general rules yet.',
  },
}

/** What the owner said, in words staff can act on. */
export function ruleErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.status === 409) {
    return 'This rule changed somewhere else. Refresh the page and try again.'
  }
  if (error instanceof ApiError && error.status === 400) {
    if (error.reason?.includes('limit')) {
      return 'This clinic has reached its rule limit. Delete rules you no longer need, or contact Flio support.'
    }
    if (error.reason?.includes('server-owned authority')) {
      return 'Rules can’t tell Ava to change booking, scheduling or system behavior, or mention internal tools and IDs. Rephrase it as guidance for patients.'
    }
  }
  return 'The rule couldn’t be saved. Try again.'
}

function preview(content: string, length = 60) {
  const line = content.replace(/\s+/g, ' ').trim()
  return line.length > length ? `${line.slice(0, length - 1)}…` : line
}

const updatedFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

export function RulesPanel({
  tenantId,
  repository,
  data,
  scope,
}: {
  tenantId: string
  repository: AssistantSettingsRepository
  data: SpecialRulesResponse
  scope: VisibleScope
}) {
  const copy = COPY[scope]
  const queryClient = useQueryClient()
  const notify = useScopedToast()
  const titleId = useId()
  const notInUseId = useId()
  const [pending, setPending] = useState<PendingReconciliation | null>(null)
  const [dialog, setDialog] = useState<
    { kind: 'add' } | { kind: 'edit'; ruleId: string } | null
  >(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [busyRule, setBusyRule] = useState<string | null>(null)
  const [showNotInUse, setShowNotInUse] = useState<boolean | null>(null)
  // A turned on/off rule moves between groups; focus follows it there.
  const [followRule, setFollowRule] = useState<string | null>(null)

  useEffect(() => {
    setPending(null)
    setDialog(null)
    setDialogError(null)
  }, [tenantId])

  const reconcile = async (response: SpecialRulesResponse) => {
    const evidence = {
      tenantId,
      revision: response.specialRules.revision,
      fingerprint: response.specialRules.collectionFingerprint,
    }
    setPending(evidence)
    await repository.reconcileVoiceRules(
      evidence.tenantId,
      evidence.revision,
      evidence.fingerprint
    )
    setPending(null)
  }

  const action = useMutation({
    mutationFn: async (request: RuleAction) => {
      let response: SpecialRulesResponse
      let affectsVoice = false
      if (request.kind === 'CREATE') {
        response = await repository.createRule(
          tenantId,
          data.specialRules.revision,
          request.content,
          scope
        )
      } else if (request.kind === 'UPDATE') {
        response = await repository.updateRule(
          tenantId,
          request.rule.ruleId,
          request.rule.revision,
          request.content
        )
        affectsVoice = request.rule.status === 'ACTIVE'
      } else if (request.kind === 'STATUS') {
        response = await repository.setRuleStatus(
          tenantId,
          request.rule.ruleId,
          request.rule.revision,
          request.status
        )
        affectsVoice = true
      } else {
        // Deleting archives the rule: it leaves every AI use, can't be
        // restored, and its record stays for the clinic's history.
        response = await repository.archiveRule(
          tenantId,
          request.rule.ruleId,
          request.rule.revision
        )
        affectsVoice = request.rule.status === 'ACTIVE'
      }
      queryClient.setQueryData(
        ['settings', tenantId, 'special-rules'],
        response
      )
      if (affectsVoice) {
        try {
          await reconcile(response)
        } catch (error) {
          throw new VoiceReconciliationPendingError(undefined, { cause: error })
        }
      }
      return response
    },
    onMutate: (request) => {
      setBusyRule(request.kind === 'CREATE' ? null : request.rule.ruleId)
    },
    onSettled: () => setBusyRule(null),
    onSuccess: (_response, request) => {
      setDialog(null)
      setDialogError(null)
      if (request.kind === 'STATUS') follow(request)
      notify('success', successMessage(request))
    },
    onError: (error, request) => {
      if (error instanceof VoiceReconciliationPendingError) {
        // The rule change is saved; only the Voice assistant lags.
        setDialog(null)
        setDialogError(null)
        if (request.kind === 'STATUS') follow(request)
        notify(
          'success',
          `${successMessage(request)}. The Voice assistant hasn’t picked it up yet.`
        )
        return
      }
      if (request.kind === 'STATUS') {
        notify(
          'error',
          error instanceof ApiError && error.status === 409
            ? ruleErrorMessage(error)
            : request.status === 'ACTIVE'
              ? 'The rule couldn’t be turned on. Refresh the page and try again.'
              : 'The rule couldn’t be turned off. Refresh the page and try again.'
        )
      } else setDialogError(ruleErrorMessage(error))
      return queryClient.invalidateQueries({
        queryKey: ['settings', tenantId, 'special-rules'],
      })
    },
  })

  function follow(request: Extract<RuleAction, { kind: 'STATUS' }>) {
    if (request.status === 'INACTIVE') setShowNotInUse(true)
    setFollowRule(request.rule.ruleId)
  }

  useEffect(() => {
    if (!followRule) return
    document
      .querySelector<HTMLElement>(`[data-rule-switch="${followRule}"]`)
      ?.focus()
    setFollowRule(null)
  }, [followRule, data])

  const retry = useMutation({
    mutationFn: async () => {
      if (!pending || pending.tenantId !== tenantId)
        throw new Error('stale tenant')
      await repository.reconcileVoiceRules(
        pending.tenantId,
        pending.revision,
        pending.fingerprint
      )
    },
    onSuccess: () => {
      setPending(null)
      notify('success', 'Voice assistant updated with the latest rules')
    },
    onError: () => notify('error', 'The Voice assistant still needs updating'),
  })

  const all = data.specialRules.rules
  const rules = all.filter(
    (rule) => rule.scope === scope && rule.status !== 'ARCHIVED'
  )
  const inUse = rules.filter((rule) => rule.status === 'ACTIVE')
  const notInUse = rules.filter((rule) => rule.status !== 'ACTIVE')
  const atLimit =
    all.filter((rule) => rule.status !== 'ARCHIVED').length >=
      RULES_IN_USE_LIMIT || all.length >= RULES_STORED_LIMIT
  const busy = action.isPending || retry.isPending
  const notInUseOpen =
    showNotInUse ?? (inUse.length === 0 || notInUse.length <= 3)
  const editing =
    dialog?.kind === 'edit'
      ? rules.find((rule) => rule.ruleId === dialog.ruleId)
      : undefined

  function row(rule: SpecialRule) {
    const textId = `${titleId}-${rule.ruleId}`
    const working = busyRule === rule.ruleId
    return (
      <li key={rule.ruleId} className={styles.ruleRow}>
        <div className={styles.ruleText}>
          <p id={textId} title={rule.content}>
            {rule.content}
          </p>
          <span className={styles.ruleMeta}>
            {rule.status === 'DRAFT' ? (
              <span className={styles.chip} data-tone="draft">
                Draft · not used yet
              </span>
            ) : rule.status === 'INACTIVE' ? (
              <span className={styles.chip}>Off</span>
            ) : null}
            <span>
              {working
                ? 'Saving…'
                : `Updated ${updatedFormat.format(rule.updatedAtMs)}`}
            </span>
          </span>
        </div>
        <div className={styles.ruleControls}>
          <Button
            type="button"
            variant="secondary"
            className={styles.editButton}
            disabled={busy}
            aria-label={`Edit rule: ${preview(rule.content)}`}
            onClick={() => {
              setDialogError(null)
              setDialog({ kind: 'edit', ruleId: rule.ruleId })
            }}
          >
            <PencilLine size={15} aria-hidden="true" /> Edit
          </Button>
          <Switch
            aria-label="Use this rule"
            aria-describedby={textId}
            data-rule-switch={rule.ruleId}
            checked={rule.status === 'ACTIVE'}
            disabled={busy}
            onChange={(event) =>
              action.mutate({
                kind: 'STATUS',
                rule,
                status: event.currentTarget.checked ? 'ACTIVE' : 'INACTIVE',
              })
            }
          />
        </div>
      </li>
    )
  }

  return (
    <section className={panel.panel} aria-labelledby={titleId}>
      <header className={panel.panelHeader}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>{copy.eyebrow}</span>
          <h2 id={titleId}>{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
        <div className={styles.headerAction}>
          <Button
            type="button"
            variant="secondary"
            disabled={busy || atLimit}
            aria-describedby={atLimit ? `${titleId}-limit` : undefined}
            onClick={() => {
              setDialogError(null)
              setDialog({ kind: 'add' })
            }}
          >
            <Plus size={16} aria-hidden="true" /> Add rule
          </Button>
          {atLimit ? (
            <small id={`${titleId}-limit`} className={panel.hint}>
              Rule limit reached.
            </small>
          ) : null}
        </div>
      </header>

      <div className={styles.rulesBody}>
        {pending ? (
          <div className={styles.syncNotice} role="status">
            <span>
              Saved. The Voice assistant hasn’t picked up the latest rules yet.
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => retry.mutate()}
            >
              <RefreshCw size={14} aria-hidden="true" /> Retry Voice sync
            </Button>
          </div>
        ) : null}

        {rules.length === 0 ? (
          <p className={styles.emptyRules}>{copy.empty}</p>
        ) : (
          <>
            <div className={styles.ruleGroup}>
              <h3 className={styles.groupHeading}>
                In use <span>{inUse.length}</span>
              </h3>
              {inUse.length ? (
                <ul className={styles.ruleList}>{inUse.map(row)}</ul>
              ) : (
                <p className={styles.emptyRules}>
                  No rule is in use. Turn a rule on to start using it.
                </p>
              )}
            </div>
            {notInUse.length ? (
              <div className={styles.ruleGroup}>
                <h3 className={styles.groupHeading}>
                  <button
                    type="button"
                    className={styles.groupToggle}
                    aria-expanded={notInUseOpen}
                    aria-controls={notInUseId}
                    onClick={() => setShowNotInUse(!notInUseOpen)}
                  >
                    Not in use <span>{notInUse.length}</span>
                    <ChevronDown
                      size={15}
                      aria-hidden="true"
                      className={panel.chevron}
                      data-open={notInUseOpen || undefined}
                    />
                  </button>
                </h3>
                {notInUseOpen ? (
                  <ul id={notInUseId} className={styles.ruleList}>
                    {notInUse.map(row)}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>

      {dialog ? (
        <RuleDialog
          title={
            dialog.kind === 'add'
              ? `Add ${copy.title.toLowerCase().replace(/s$/, '')}`
              : `Edit ${copy.title.toLowerCase().replace(/s$/, '')}`
          }
          eyebrow={copy.title}
          rule={editing}
          saving={action.isPending}
          error={dialogError}
          onSubmit={(content) => {
            setDialogError(null)
            action.mutate(
              editing
                ? { kind: 'UPDATE', rule: editing, content }
                : { kind: 'CREATE', content }
            )
          }}
          onDelete={
            editing
              ? () => {
                  setDialogError(null)
                  action.mutate({ kind: 'DELETE', rule: editing })
                }
              : undefined
          }
          onClose={() => {
            if (action.isPending) return
            setDialog(null)
            setDialogError(null)
          }}
        />
      ) : null}
    </section>
  )
}

function successMessage(request: RuleAction) {
  if (request.kind === 'CREATE') return 'Rule added as a draft'
  if (request.kind === 'STATUS') {
    return request.status === 'ACTIVE' ? 'Rule turned on' : 'Rule turned off'
  }
  if (request.kind === 'DELETE') return 'Rule deleted'
  return request.rule.status === 'ACTIVE'
    ? 'Rule updated and in use'
    : 'Rule saved as a draft'
}
